/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import type { AuthTokenProvider, CsrfToken } from '@amzn/vinyl-amzn'
import type { ObservableValue } from '@amazon/vinyl-observable'

export enum AuthStatus {
    /**
     * The customer is currently signed in.
     */
    SIGNED_IN = 'signedIn',

    /**
     * The customer is currently signed out.
     */
    SIGNED_OUT = 'signedOut',

    /**
     * The authentication state is pending.
     */
    PENDING = 'pending',
}

export interface Auth {
    readonly status: ObservableValue<AuthStatus>

    /**
     * An alias to `status.value === AuthStatus.SIGNED_IN`
     */
    isSignedIn(): boolean

    /**
     * Returns the auth token provider, or throws if not signed in.
     */
    get authTokenProvider(): AuthTokenProvider

    /**
     * Whether this controller authenticates requests with a bearer token in the
     * `authorization` header. Token controllers (LWA/MAP/CBL) return true;
     * cookie-session controllers (AuthPortal) return false because the request
     * is authenticated by the session cookie + CSRF triple, and a bearer
     * alongside it 401s. The gql layer attaches the bearer only when this is
     * true.
     */
    usesBearer(): boolean

    signIn(): void
    /** Runs the session-end hooks while still signed in, then drops the session. */
    signOut(): Promise<void>

    /**
     * Optional request-decoration capability for cookie-session controllers.
     *
     * A cookie-session auth (AuthPortal) authenticates the request itself with
     * a first-party session cookie plus a CSRF triple, rather than a bearer
     * token in the `authorization` header. Token controllers (LWA/MAP/CBL)
     * leave both undefined and are unaffected — the gql layer only decorates a
     * request when these are present.
     *
     * `requestCredentials` is the `credentials` mode the request must use so
     * the session cookie travels (`'include'`); `getCsrfHeaders()` returns the
     * `csrf-rnd`/`csrf-token`/`csrf-ts` headers, or `undefined` when no CSRF
     * triple is available.
     */
    readonly requestCredentials?: RequestCredentials
    getCsrfHeaders?(): Record<string, string> | undefined

    /**
     * The CSRF triple as a vinyl-amzn `CsrfToken` (`{token, rnd, ts}`) for the
     * player/DMLS streaming path, which wants the object — not headers — via
     * `DmlsOptions.csrfTokenProvider`. Cookie-session controllers (AuthPortal)
     * return it; token controllers (LWA/MAP/CBL) leave it undefined so the
     * bearer streaming path is unchanged.
     */
    getCsrfToken?(): CsrfToken | undefined

    /**
     * Optional customer-id capability for cookie-session controllers.
     *
     * AuthPortal's `/config.json` carries the signed-in `customerId`; a
     * cookie-session controller exposes it here so the customer read can avoid
     * a Stratus round-trip. Token controllers (LWA/MAP/CBL) leave it undefined:
     * their customer id comes from Stratus.
     */
    getCustomerId?(): string | null

    /**
     * Optional Montana-CSRF capability for cookie-session controllers.
     *
     * The Audible SDK's LPH (licensing) endpoint expects the Montana CSRF, a
     * token distinct from the CSRF triple. AuthPortal's `/config.json` carries
     * it; a cookie-session controller exposes it here. Token controllers
     * (LWA/MAP/CBL) leave it undefined, so the SDK config falls back to `''`.
     */
    getMontanaCsrf?(): string | undefined

    /**
     * Optional cookie-freshness provider for cookie-session controllers.
     *
     * DMLS's `authCookie` accepts a `() => Promise<true>` that resolves only
     * once the session cookie is fresh, refreshing ahead of expiry and
     * coalescing concurrent callers onto one refresh. AuthPortal returns one so
     * a stream never fires against an about-to-lapse cookie; token controllers
     * (LWA/MAP/CBL) leave it undefined and DMLS uses the bearer path.
     */
    getAuthCookieProvider?(): () => Promise<true>
}
